"use client"

import { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Loader2 } from "lucide-react"

export type SuspensionMarkMode = "now" | "retroactive"

export interface SuspensionMarkPayload {
  mode: SuspensionMarkMode
  suspensionDate?: string
  /** Protocolo TIM desta suspensão (cada re-suspensão gera protocolo novo) */
  protocolo?: string
}

interface SuspensionMarkDialogProps {
  open: boolean
  lineCount: number
  onClose: () => void
  onConfirm: (payload: SuspensionMarkPayload) => Promise<void>
}

const dateToInputValue = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export function SuspensionMarkDialog({
  open,
  lineCount,
  onClose,
  onConfirm,
}: SuspensionMarkDialogProps) {
  const [mode, setMode] = useState<SuspensionMarkMode>("now")
  const [retroactiveDate, setRetroactiveDate] = useState(dateToInputValue(new Date()))
  const [protocolo, setProtocolo] = useState("")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setMode("now")
    setRetroactiveDate(dateToInputValue(new Date()))
    setProtocolo("")
    setSubmitting(false)
  }, [open])

  const handleSubmit = async () => {
    if (mode === "retroactive" && !retroactiveDate) {
      window.alert("Informe a data em que a suspensão foi feita na TIM.")
      return
    }

    setSubmitting(true)
    try {
      const digits = protocolo.replace(/\D/g, "")
      await onConfirm({
        mode,
        suspensionDate: mode === "retroactive" ? retroactiveDate : undefined,
        protocolo: digits.length >= 10 ? digits : undefined,
      })
      onClose()
    } catch {
      // O pai já mostra o toast. Não fechar e não relançar (evita onunhandledrejection).
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Marcar como suspensa</DialogTitle>
          <DialogDescription>
            {lineCount === 1
              ? "Confirme como registar a suspensão desta linha."
              : `Confirme como registar a suspensão de ${lineCount} linhas.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Intenção</Label>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="suspensionMode"
                  checked={mode === "now"}
                  onChange={() => setMode("now")}
                />
                Marcar como suspenso agora (data de hoje)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="suspensionMode"
                  checked={mode === "retroactive"}
                  onChange={() => setMode("retroactive")}
                />
                Marcar data anterior de suspensão (TIM)
              </label>
            </div>
          </div>

          {mode === "retroactive" && (
            <div className="space-y-1">
              <Label htmlFor="retroactiveDate">Data em que foi suspensa (TIM)</Label>
              <Input
                id="retroactiveDate"
                type="date"
                max={dateToInputValue(new Date())}
                value={retroactiveDate}
                onChange={(e) => setRetroactiveDate(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="timProtocolo">Protocolo TIM (opcional)</Label>
            <Input
              id="timProtocolo"
              placeholder="Número do protocolo após suspender no portal"
              value={protocolo}
              onChange={(e) => setProtocolo(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Cada suspensão ou re-suspensão gera um protocolo diferente. Guarde todos no histórico do contrato.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
